import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { 
  Plus, 
  Trash2, 
  Save, 
  FileText, 
  Calendar,
  User,
  Phone,
  Mail,
  Target,
  AlertTriangle,
  TrendingUp,
  CheckCircle
} from 'lucide-react';
import { useToast } from '../../../components/Toast';

interface WorkItem {
  id: string;
  task?: string;
  risk?: string;
  notes: string;
  priority: 'high' | 'medium' | 'low';
  status: 'active' | 'completed' | 'cancelled';
  assigned_to?: string;
  due_date?: string;
}

interface Metric {
  id: string;
  metric_name: string;
  current_value: string;
  target_value: string;
  status: 'on-track' | 'at-risk' | 'behind';
  trend: 'improving' | 'stable' | 'declining';
  unit: string;
  notes: string;
}

interface WSRData {
  client_id: number;
  week_ending: string;
  reporting_period: string;
  reporting_date: string;
  project_sponsor: string;
  prepared_by: string;
  phone: string;
  email: string;
  report_summary: string;
  conclusions_recommendations: string;
  status: 'draft' | 'submitted' | 'approved';
  accomplished: WorkItem[];
  targets: WorkItem[];
  risks: WorkItem[];
  metrics: Metric[];
  originalFileName?: string;
  originalFileType?: string;
  originalFileSize?: number;
}

interface WSRGeneratorProps {
  clientId: number;
  onSave: (wsrData: WSRData) => Promise<void>;
  initialData?: Partial<WSRData>;
  uploadedAnalysis?: any; // Data from centralized upload handler
}

const WSRGenerator: React.FC<WSRGeneratorProps> = ({ 
  clientId, 
  onSave, 
  initialData,
  uploadedAnalysis 
}) => {
  const [wsrData, setWSRData] = useState<WSRData>({
    client_id: clientId,
    week_ending: '',
    reporting_period: '',
    reporting_date: new Date().toISOString().split('T')[0],
    project_sponsor: 'DJ Dhananjay',
    prepared_by: 'Kiro AI Assistant',
    phone: '(425) 749-2101',
    email: 'kiro_ai@costco.com',
    report_summary: '',
    conclusions_recommendations: '',
    status: 'draft',
    accomplished: [],
    targets: [],
    risks: [],
    metrics: []
  });

  const [isSaving, setIsSaving] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    if (initialData) {
      setWSRData(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  // Handle uploaded analysis from centralized upload
  useEffect(() => {
    if (uploadedAnalysis) {
      handleAnalysisComplete(uploadedAnalysis);
    }
  }, [uploadedAnalysis]);

  // Auto-calculate week ending and reporting period
  useEffect(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysToFriday = dayOfWeek <= 5 ? 5 - dayOfWeek : 7 - dayOfWeek + 5;
    const friday = new Date(today);
    friday.setDate(today.getDate() + daysToFriday);
    
    const weekEnding = friday.toISOString().split('T')[0];
    const monday = new Date(friday);
    monday.setDate(friday.getDate() - 4);
    
    const reportingPeriod = `Week Ending (WE) of ${friday.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    })}`;

    setWSRData(prev => ({
      ...prev,
      week_ending: weekEnding,
      reporting_period: reportingPeriod
    }));
  }, []);

  const handleAnalysisComplete = (analysis: any) => {
    setWSRData(prev => ({
      ...prev,
      week_ending: analysis.weekEnding || prev.week_ending,
      reporting_period: analysis.reportingPeriod || prev.reporting_period,
      project_sponsor: analysis.projectSponsor || prev.project_sponsor,
      prepared_by: analysis.preparedBy || prev.prepared_by,
      phone: analysis.phone || prev.phone,
      email: analysis.email || prev.email,
      report_summary: analysis.reportSummary || prev.report_summary,
      conclusions_recommendations: analysis.conclusionsRecommendations || prev.conclusions_recommendations,
      accomplished: analysis.workAccomplished?.map((item: any, index: number) => ({
        id: `acc_${index}`,
        task: item.task,
        notes: item.notes || '',
        priority: 'medium' as const,
        status: 'completed' as const
      })) || prev.accomplished,
      targets: analysis.nextWeekTargets?.map((item: any, index: number) => ({
        id: `tgt_${index}`,
        task: item.task,
        notes: item.notes || '',
        priority: item.priority || 'medium',
        status: 'active' as const
      })) || prev.targets,
      risks: analysis.risks?.map((item: any, index: number) => ({
        id: `risk_${index}`,
        risk: item.risk,
        notes: item.notes || '',
        priority: item.priority || 'medium',
        status: 'active' as const
      })) || prev.risks,
      metrics: analysis.metrics?.map((item: any, index: number) => ({
        id: `metric_${index}`,
        metric_name: item.metric_name,
        current_value: item.current_value,
        target_value: item.target_value || '',
        status: item.status || 'on-track',
        trend: item.trend || 'stable',
        unit: item.unit || '',
        notes: item.notes || ''
      })) || prev.metrics,
      // Store file information from uploaded analysis
      originalFileName: analysis.originalFileName,
      originalFileType: analysis.originalFileType,
      originalFileSize: analysis.originalFileSize
    }));
  };

  const addWorkItem = (category: 'accomplished' | 'targets' | 'risks') => {
    const newItem: WorkItem = {
      id: `${category}_${Date.now()}`,
      task: category === 'risks' ? undefined : '',
      risk: category === 'risks' ? '' : undefined,
      notes: '',
      priority: 'medium',
      status: category === 'accomplished' ? 'completed' : 'active'
    };

    setWSRData(prev => ({
      ...prev,
      [category]: [...prev[category], newItem]
    }));
  };

  const updateWorkItem = (category: 'accomplished' | 'targets' | 'risks', id: string, updates: Partial<WorkItem>) => {
    setWSRData(prev => ({
      ...prev,
      [category]: prev[category].map(item => 
        item.id === id ? { ...item, ...updates } : item
      )
    }));
  };

  const removeWorkItem = (category: 'accomplished' | 'targets' | 'risks', id: string) => {
    setWSRData(prev => ({
      ...prev,
      [category]: prev[category].filter(item => item.id !== id)
    }));
  };

  const addMetric = () => {
    const newMetric: Metric = {
      id: `metric_${Date.now()}`,
      metric_name: '',
      current_value: '',
      target_value: '',
      status: 'on-track',
      trend: 'stable',
      unit: '',
      notes: ''
    };

    setWSRData(prev => ({
      ...prev,
      metrics: [...prev.metrics, newMetric]
    }));
  };

  const updateMetric = (id: string, updates: Partial<Metric>) => {
    setWSRData(prev => ({
      ...prev,
      metrics: prev.metrics.map(metric => 
        metric.id === id ? { ...metric, ...updates } : metric
      )
    }));
  };

  const removeMetric = (id: string) => {
    setWSRData(prev => ({
      ...prev,
      metrics: prev.metrics.filter(metric => metric.id !== id)
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(wsrData);
      addToast('✅ WSR saved successfully', 'success');
    } catch (error) {
      console.error('Error saving WSR:', error);
      addToast('❌ Error saving WSR', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on-track': return 'bg-green-100 text-green-800';
      case 'at-risk': return 'bg-yellow-100 text-yellow-800';
      case 'behind': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Weekly Status Report Generator</h1>
        <Button 
          onClick={handleSave} 
          disabled={isSaving}
          className="flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Saving...' : 'Save WSR'}
        </Button>
      </div>

      {uploadedAnalysis && (
        <Card className="bg-shiftleft-50 border-shiftleft-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-shiftleft-800">
                <CheckCircle className="w-5 h-5" />
                <div>
                  <span className="font-medium">Previous WSR data loaded successfully</span>
                  {wsrData.originalFileName && (
                    <div className="text-sm text-shiftleft-600 mt-1">
                      📄 From file: {wsrData.originalFileName} 
                      {wsrData.originalFileSize && (
                        <span className="ml-2">({(wsrData.originalFileSize / 1024).toFixed(1)} KB)</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {wsrData.originalFileName && (
                <Badge variant="outline" className="text-shiftleft-700 border-shiftleft-300">
                  {wsrData.originalFileType?.includes('pdf') ? 'PDF' : 
                   wsrData.originalFileType?.includes('markdown') ? 'Markdown' : 'Text'}
                </Badge>
              )}
            </div>
            <p className="text-sm text-shiftleft-600 mt-1">
              Data from uploaded WSR has been pre-populated in the form below.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Header Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            WSR Header Information
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Week Ending
            </label>
            <Input
              type="date"
              value={wsrData.week_ending}
              onChange={(e) => setWSRData(prev => ({ ...prev, week_ending: e.target.value }))}
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Reporting Period</label>
            <Input
              value={wsrData.reporting_period}
              onChange={(e) => setWSRData(prev => ({ ...prev, reporting_period: e.target.value }))}
              placeholder="Week Ending (WE) of..."
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Reporting Date</label>
            <Input
              type="date"
              value={wsrData.reporting_date}
              onChange={(e) => setWSRData(prev => ({ ...prev, reporting_date: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <User className="w-4 h-4" />
              Project Sponsor
            </label>
            <Input
              value={wsrData.project_sponsor}
              onChange={(e) => setWSRData(prev => ({ ...prev, project_sponsor: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Prepared By</label>
            <Input
              value={wsrData.prepared_by}
              onChange={(e) => setWSRData(prev => ({ ...prev, prepared_by: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Phone
            </label>
            <Input
              value={wsrData.phone}
              onChange={(e) => setWSRData(prev => ({ ...prev, phone: e.target.value }))}
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </label>
            <Input
              type="email"
              value={wsrData.email}
              onChange={(e) => setWSRData(prev => ({ ...prev, email: e.target.value }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Report Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Report Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={wsrData.report_summary}
            onChange={(e) => setWSRData(prev => ({ ...prev, report_summary: e.target.value }))}
            placeholder="Brief overview of the week's activities and key highlights..."
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Work Accomplished */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Work Accomplished
            </span>
            <Button onClick={() => addWorkItem('accomplished')} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Task
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {wsrData.accomplished.map((item) => (
            <div key={item.id} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-start justify-between">
                <Input
                  value={item.task || ''}
                  onChange={(e) => updateWorkItem('accomplished', item.id, { task: e.target.value })}
                  placeholder="Task description..."
                  className="flex-1 mr-2"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeWorkItem('accomplished', item.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <Textarea
                value={item.notes}
                onChange={(e) => updateWorkItem('accomplished', item.id, { notes: e.target.value })}
                placeholder="Additional notes or details..."
                rows={2}
              />
            </div>
          ))}
          {wsrData.accomplished.length === 0 && (
            <p className="text-gray-500 text-center py-4">No accomplished tasks yet. Click "Add Task" to get started.</p>
          )}
        </CardContent>
      </Card>

      {/* Next Week Targets */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Next Week Targets
            </span>
            <Button onClick={() => addWorkItem('targets')} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Target
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {wsrData.targets.map((item) => (
            <div key={item.id} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-start justify-between">
                <Input
                  value={item.task || ''}
                  onChange={(e) => updateWorkItem('targets', item.id, { task: e.target.value })}
                  placeholder="Target task description..."
                  className="flex-1 mr-2"
                />
                <div className="flex items-center gap-2">
                  <Select
                    value={item.priority}
                    onValueChange={(value: 'high' | 'medium' | 'low') => 
                      updateWorkItem('targets', item.id, { priority: value })
                    }
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeWorkItem('targets', item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <Textarea
                value={item.notes}
                onChange={(e) => updateWorkItem('targets', item.id, { notes: e.target.value })}
                placeholder="Additional notes or details..."
                rows={2}
              />
              <Badge className={getPriorityColor(item.priority)}>
                {item.priority.toUpperCase()} PRIORITY
              </Badge>
            </div>
          ))}
          {wsrData.targets.length === 0 && (
            <p className="text-gray-500 text-center py-4">No targets set yet. Click "Add Target" to get started.</p>
          )}
        </CardContent>
      </Card>

      {/* Risks & Bottlenecks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Risks & Bottlenecks
            </span>
            <Button onClick={() => addWorkItem('risks')} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Risk
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {wsrData.risks.map((item) => (
            <div key={item.id} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-start justify-between">
                <Input
                  value={item.risk || ''}
                  onChange={(e) => updateWorkItem('risks', item.id, { risk: e.target.value })}
                  placeholder="Risk or bottleneck description..."
                  className="flex-1 mr-2"
                />
                <div className="flex items-center gap-2">
                  <Select
                    value={item.priority}
                    onValueChange={(value: 'high' | 'medium' | 'low') => 
                      updateWorkItem('risks', item.id, { priority: value })
                    }
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeWorkItem('risks', item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <Textarea
                value={item.notes}
                onChange={(e) => updateWorkItem('risks', item.id, { notes: e.target.value })}
                placeholder="Mitigation strategies or additional notes..."
                rows={2}
              />
              <Badge className={getPriorityColor(item.priority)}>
                {item.priority.toUpperCase()} RISK
              </Badge>
            </div>
          ))}
          {wsrData.risks.length === 0 && (
            <p className="text-gray-500 text-center py-4">No risks identified yet. Click "Add Risk" to get started.</p>
          )}
        </CardContent>
      </Card>

      {/* Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Key Performance Metrics
            </span>
            <Button onClick={addMetric} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Metric
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {wsrData.metrics.map((metric) => (
            <div key={metric.id} className="border rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input
                  value={metric.metric_name}
                  onChange={(e) => updateMetric(metric.id, { metric_name: e.target.value })}
                  placeholder="Metric name..."
                />
                <Input
                  value={metric.current_value}
                  onChange={(e) => updateMetric(metric.id, { current_value: e.target.value })}
                  placeholder="Current value..."
                />
                <Input
                  value={metric.target_value}
                  onChange={(e) => updateMetric(metric.id, { target_value: e.target.value })}
                  placeholder="Target value..."
                />
                <div className="flex items-center gap-2">
                  <Select
                    value={metric.status}
                    onValueChange={(value: 'on-track' | 'at-risk' | 'behind') => 
                      updateMetric(metric.id, { status: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="on-track">On Track</SelectItem>
                      <SelectItem value="at-risk">At Risk</SelectItem>
                      <SelectItem value="behind">Behind</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeMetric(metric.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Select
                  value={metric.trend}
                  onValueChange={(value: 'improving' | 'stable' | 'declining') => 
                    updateMetric(metric.id, { trend: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Trend..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="improving">Improving</SelectItem>
                    <SelectItem value="stable">Stable</SelectItem>
                    <SelectItem value="declining">Declining</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={metric.unit}
                  onChange={(e) => updateMetric(metric.id, { unit: e.target.value })}
                  placeholder="Unit (%, ms, etc.)"
                />
              </div>
              <Textarea
                value={metric.notes}
                onChange={(e) => updateMetric(metric.id, { notes: e.target.value })}
                placeholder="Additional notes about this metric..."
                rows={2}
              />
              <Badge className={getStatusColor(metric.status)}>
                {metric.status.toUpperCase().replace('-', ' ')}
              </Badge>
            </div>
          ))}
          {wsrData.metrics.length === 0 && (
            <p className="text-gray-500 text-center py-4">No metrics added yet. Click "Add Metric" to get started.</p>
          )}
        </CardContent>
      </Card>

      {/* Conclusions & Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle>Conclusions & Recommendations</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={wsrData.conclusions_recommendations}
            onChange={(e) => setWSRData(prev => ({ ...prev, conclusions_recommendations: e.target.value }))}
            placeholder="Summary of key findings, recommendations, and next steps..."
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={isSaving}
          size="lg"
          className="flex items-center gap-2"
        >
          <Save className="w-5 h-5" />
          {isSaving ? 'Saving WSR...' : 'Save WSR'}
        </Button>
      </div>
    </div>
  );
};

export default WSRGenerator;